import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './App.css';

const API_BASE = 'https://reo-binding-api.severnaya-social.workers.dev';

const PILLAR_COLORS = {
  seo: '#3b82f6',
  geo: '#10b981',
  aeo: '#8b5cf6',
  reo: '#ef4444',
  feo: '#f59e0b'
};

function App() {
  const svgRef = useRef();
  const [nodes, setNodes] = useState([]);
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pillarFilter, setPillarFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);

  useEffect(() => {
    fetchKGData();
  }, []);

  const fetchKGData = async () => {
    try {
      setLoading(true);
      
      const res = await fetch(`${API_BASE}/api/kg`);
      const text = await res.text();
      
      const lines = text.trim().split('\n').filter(line => line.trim());
      const allNodes = lines.map(line => JSON.parse(line));
      
      console.log(`Loaded ${allNodes.length} nodes from KG`);
      
      const nodeData = allNodes.map(node => ({
        id: node.id,
        topic: node.topic,
        pillar: node.pillar,
        concepts: node.concepts || [],
        similarity: 1.0,
        layers: node.layers || [],
        meta: node.meta || {},
        recursive_depth: node.recursive_depth,
        timestamp: node.timestamp
      }));

      setNodes(nodeData);
      
      const linkData = [];
      const linkSet = new Set();
      
      allNodes.forEach(node => {
        if (node.edges && Array.isArray(node.edges)) {
          node.edges.slice(0, 10).forEach(edge => {
            const sourceId = edge.source || node.id;
            const targetId = edge.target || edge.target_id;
            const linkKey = `${sourceId}-${targetId}`;
            
            if (!linkSet.has(linkKey) && sourceId !== targetId) {
              linkSet.add(linkKey);
              linkData.push({
                source: sourceId,
                target: targetId,
                value: edge.weight || 1
              });
            }
          });
        }
      });
      
      setLinks(linkData);
      
      setStats({
        total_nodes: nodeData.length,
        total_edges: linkData.length,
        avg_edges_per_node: Math.round(linkData.length / nodeData.length)
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to load KG data:', err);
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setIsSearchActive(false);
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = nodes.filter(node => 
      node.topic.toLowerCase().includes(query) ||
      node.concepts.some(c => c.toLowerCase().includes(query))
    );
    
    const resultIds = results.map(r => r.id);
    setSearchResults(resultIds);
    setIsSearchActive(true);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearchActive(false);
  };

  useEffect(() => {
    if (nodes.length === 0 || !svgRef.current) return;

    const width = window.innerWidth;
    const height = window.innerHeight - 100;

    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .call(d3.zoom()
        .scaleExtent([0.1, 4])
        .on('zoom', (event) => {
          container.attr('transform', event.transform);
        }));

    const container = svg.append('g');

    // SMOOTHER SIMULATION with slower, more cinematic movement
    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(150).strength(0.3))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(40).strength(0.7))
      .force('x', d3.forceX(width / 2).strength(0.05))
      .force('y', d3.forceY(height / 2).strength(0.05))
      .velocityDecay(0.6)  // Smoother damping
      .alphaDecay(0.01);   // Slower convergence

    const link = container.append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('class', 'graph-link')
      .attr('stroke', d => {
        const sourceNode = nodes.find(n => n.id === d.source.id || n.id === d.source);
        return PILLAR_COLORS[sourceNode?.pillar] || '#333';
      })
      .attr('stroke-opacity', 0.25)
      .attr('stroke-width', d => d.value * 0.8);

    const node = container.append('g')
      .selectAll('circle')
      .data(nodes)
      .join('circle')
      .attr('class', 'graph-node')
      .attr('data-id', d => d.id)
      .attr('r', 10)
      .attr('fill', d => PILLAR_COLORS[d.pillar] || '#666')
      .attr('stroke', d => PILLAR_COLORS[d.pillar] || '#fff')
      .attr('stroke-width', 2.5)
      .style('cursor', 'pointer')
      .style('filter', d => `drop-shadow(0 0 6px ${PILLAR_COLORS[d.pillar]})`)
      .call(drag(simulation))
      .on('click', (event, d) => {
        event.stopPropagation();
        fetchNodeDetail(d.id);
      })
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(300)
          .ease(d3.easeCubicOut)
          .attr('r', 14)
          .attr('stroke-width', 3.5);
        showTooltip(event, d);
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(300)
          .ease(d3.easeCubicOut)
          .attr('r', 10)
          .attr('stroke-width', 2.5);
        hideTooltip();
      });

    simulation.on('tick', () => {
      link
        .attr('x1', d => d.source.x)
        .attr('y1', d => d.source.y)
        .attr('x2', d => d.target.x)
        .attr('y2', d => d.target.y);

      node
        .attr('cx', d => d.x)
        .attr('cy', d => d.y);
    });

    // Let it settle naturally instead of forcing stop
    setTimeout(() => {
      simulation.alpha(0.3).restart();
    }, 10000);

    function drag(simulation) {
      function dragstarted(event) {
        if (!event.active) simulation.alphaTarget(0.1).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }

      function dragged(event) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }

      function dragended(event) {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      }

      return d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended);
    }

    return () => {
      simulation.stop();
    };

  }, [nodes, links]);

  useEffect(() => {
    if (!isSearchActive) {
      d3.selectAll('.graph-node')
        .attr('opacity', 1)
        .attr('r', 10)
        .style('filter', d => `drop-shadow(0 0 6px ${PILLAR_COLORS[d.pillar]})`);
      
      d3.selectAll('.graph-link')
        .attr('stroke-opacity', 0.25);
      
      return;
    }

    d3.selectAll('.graph-node')
      .attr('opacity', 0.15)
      .attr('r', 8)
      .style('filter', 'none');
    
    d3.selectAll('.graph-link')
      .attr('stroke-opacity', 0.05);

    searchResults.forEach(id => {
      d3.select(`[data-id="${id}"]`)
        .attr('opacity', 1)
        .attr('r', 14)
        .style('filter', d => `drop-shadow(0 0 15px ${PILLAR_COLORS[d.pillar]})`);
    });

    d3.selectAll('.graph-link')
      .filter(d => {
        const sourceId = d.source.id || d.source;
        const targetId = d.target.id || d.target;
        return searchResults.includes(sourceId) && searchResults.includes(targetId);
      })
      .attr('stroke-opacity', 0.8)
      .attr('stroke-width', 3);

  }, [isSearchActive, searchResults]);

  const fetchNodeDetail = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    if (node) {
      console.log('Selected node:', node); // Debug
      setSelectedNode(node);
    }
  };

  const showTooltip = (event, d) => {
    const tooltip = d3.select('#tooltip');
    tooltip
      .style('opacity', 1)
      .style('left', `${event.pageX + 10}px`)
      .style('top', `${event.pageY - 10}px`)
      .html(`
        <div class="tooltip-title">${d.topic}</div>
        <div class="tooltip-meta">
          <span class="tooltip-pillar" style="color: ${PILLAR_COLORS[d.pillar]}">${d.pillar.toUpperCase()}</span>
          <span class="tooltip-sim">Depth: ${d.recursive_depth}</span>
        </div>
      `);
  };

  const hideTooltip = () => {
    d3.select('#tooltip').style('opacity', 0);
  };

  return (
    <div className="app">
      <div className="scanlines"></div>
      <div className="grid-overlay"></div>
      
      <header className="header">
        <div className="header-left">
          <h1>
            <span className="icon">⬢</span>
            REO INTELLIGENCE GRID
          </h1>
          <div className="status-indicator">
            <span className="pulse"></span>
            <span className="status-text">SYSTEM ACTIVE</span>
          </div>
        </div>
        
        <form className="controls" onSubmit={handleSearch}>
          <div className="search-wrapper">
            <input
              type="text"
              placeholder="QUERY SEMANTIC NETWORK..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {isSearchActive && (
              <button type="button" onClick={clearSearch} className="clear-btn">
                ✕
              </button>
            )}
          </div>
          <button type="submit" className="search-btn">
            SEARCH
          </button>
          <select 
            value={pillarFilter} 
            onChange={(e) => setPillarFilter(e.target.value)}
            className="pillar-select"
          >
            <option value="all">ALL DOMAINS</option>
            <option value="seo">SEO</option>
            <option value="geo">GEO</option>
            <option value="aeo">AEO</option>
            <option value="reo">REO</option>
            <option value="feo">FEO</option>
          </select>
        </form>
        
        {stats && (
          <div className="stats">
            <div className="stat-item">
              <div className="stat-value">{stats.total_nodes}</div>
              <div className="stat-label">NODES</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{(stats.total_edges / 1000).toFixed(1)}K</div>
              <div className="stat-label">EDGES</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{stats.avg_edges_per_node}</div>
              <div className="stat-label">AVG LINKS</div>
            </div>
          </div>
        )}
      </header>

      {isSearchActive && (
        <div className="search-info">
          Found {searchResults.length} nodes matching "{searchQuery}"
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <div>INITIALIZING KNOWLEDGE GRID...</div>
        </div>
      ) : (
        <svg ref={svgRef}></svg>
      )}

      <div id="tooltip" className="tooltip"></div>

      {selectedNode && (
        <div className="modal" onClick={() => setSelectedNode(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedNode(null)}>✕</button>
            <div className="modal-header">
              <h2>{selectedNode.topic}</h2>
              <div className="node-meta">
                <span className="pillar-badge" style={{ background: PILLAR_COLORS[selectedNode.pillar] }}>
                  {selectedNode.pillar.toUpperCase()}
                </span>
                <span className="meta-chip">{selectedNode.layers?.length || 0} LAYERS</span>
                <span className="meta-chip">{selectedNode.meta?.word_total || 0} WORDS</span>
                <span className="meta-chip">DEPTH: {selectedNode.recursive_depth || 5}</span>
              </div>
            </div>
            
            {selectedNode.layers && selectedNode.layers.length > 0 ? (
              <div className="layers">
                {selectedNode.layers.map((layer, i) => (
                  <div key={i} className="layer">
                    <div className="layer-header">
                      <span className="layer-number">L{layer.layer_number || i + 1}</span>
                      <h3>{(layer.layer_type || 'LAYER').toUpperCase()}</h3>
                      <span className="layer-words">{layer.word_count || 0}w</span>
                    </div>
                    <p>{layer.content || 'No content available'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="layers">
                <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>
                  No layer content available for this node.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="legend">
        <div className="legend-title">OPTIMIZATION DOMAINS</div>
        {Object.entries(PILLAR_COLORS).map(([pillar, color]) => (
          <div key={pillar} className="legend-item">
            <div className="legend-color" style={{ background: color, boxShadow: `0 0 8px ${color}` }}></div>
            <span>{pillar.toUpperCase()}</span>
          </div>
        ))}
      </div>

      <div className="coordinates">
        LAT: 37.7749° N | LONG: 122.4194° W | ALT: {stats?.total_nodes || 0} NODES
      </div>
    </div>
  );
}

export default App;